import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface RecordDiff {
  id: string;
  name?: string;
  status: 'added' | 'modified' | 'deleted' | 'unchanged';
  thumbnail?: string;
  mediaType?: 'image' | 'video';
  changes?: Array<{
    field: string;
    staging: any;
    production: any;
  }>;
}

interface TableComparison {
  added: number;
  modified: number;
  deleted: number;
  unchanged: number;
  records: RecordDiff[];
}

interface CompareResult {
  totalChanges: number;
  warnings?: string[];
  comparison: {
    products: TableComparison;
    inventory: TableComparison;
    media: TableComparison;
    copy: TableComparison;
    product_translations: TableComparison;
    media_alt_text: TableComparison;
  };
}

export function CMSDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncingTable, setSyncingTable] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState<CompareResult | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [expandedTable, setExpandedTable] = useState<'products' | 'inventory' | 'media' | 'copy' | 'product_translations' | 'media_alt_text' | null>(null);

  function handleLogout() {
    logout();
    navigate('/cms/login');
  }

  async function fetchComparison() {
    setComparing(true);
    setCompareError(null);

    try {
      const token = localStorage.getItem('cms_token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/cms/sync', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to compare databases');
      }

      setComparison(data);
    } catch (error) {
      console.error('Compare error:', error);
      setCompareError(error instanceof Error ? error.message : 'Failed to compare databases');
    } finally {
      setComparing(false);
    }
  }

  // Fetch comparison on mount
  useEffect(() => {
    fetchComparison();
  }, []);

  const TABLE_RECORD_TYPES: Record<string, string[]> = {
    products: ['products'],
    inventory: ['inventory'],
    media: ['media', 'media_alt_text'],
    copy: ['copy'],
    product_translations: ['product_translations'],
    media_alt_text: ['media_alt_text'],
  };

  async function handleSyncTable(table: string) {
    setSyncingTable(table);
    setSyncStatus(null);

    try {
      const token = localStorage.getItem('cms_token');
      if (!token) throw new Error('Not authenticated');

      const recordTypes = TABLE_RECORD_TYPES[table] || [table];

      for (const recordType of recordTypes) {
        const response = await fetch('/api/cms/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ recordType, allRecords: true }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || `Failed to sync ${recordType}`);
      }

      const label = table === 'product_translations' ? 'translations' : table;
      setSyncStatus({ type: 'success', message: `${label} synced to production successfully.` });
      await fetchComparison();
    } catch (error) {
      setSyncStatus({
        type: 'error',
        message: error instanceof Error ? error.message : `Failed to sync ${table}`,
      });
    } finally {
      setSyncingTable(null);
    }
  }

  async function handleSyncToProduction() {
    setSyncing(true);
    setSyncStatus(null);

    try {
      const token = localStorage.getItem('cms_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Sync Products
      const productsResponse = await fetch('/api/cms/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recordType: 'products', allRecords: true }),
      });

      const productsData = await productsResponse.json();
      if (!productsResponse.ok) {
        throw new Error(productsData.error || 'Failed to sync products');
      }

      // Sync Inventory
      const inventoryResponse = await fetch('/api/cms/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recordType: 'inventory', allRecords: true }),
      });

      const inventoryData = await inventoryResponse.json();
      if (!inventoryResponse.ok) {
        throw new Error(inventoryData.error || 'Failed to sync inventory');
      }

      // Sync Media
      const mediaResponse = await fetch('/api/cms/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recordType: 'media', allRecords: true }),
      });

      const mediaData = await mediaResponse.json();
      if (!mediaResponse.ok) {
        throw new Error(mediaData.error || 'Failed to sync media');
      }

      // Sync Copy
      const copyResponse = await fetch('/api/cms/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recordType: 'copy', allRecords: true }),
      });

      const copyData = await copyResponse.json();
      if (!copyResponse.ok) {
        throw new Error(copyData.error || 'Failed to sync copy');
      }

      // Sync Product Translations
      const translationsResponse = await fetch('/api/cms/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recordType: 'product_translations', allRecords: true }),
      });

      const translationsData = await translationsResponse.json();
      if (!translationsResponse.ok) {
        throw new Error(translationsData.error || 'Failed to sync product translations');
      }

      // Sync Media Alt Text
      const mediaAltTextResponse = await fetch('/api/cms/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recordType: 'media_alt_text', allRecords: true }),
      });

      const mediaAltTextData = await mediaAltTextResponse.json();
      if (!mediaAltTextResponse.ok) {
        throw new Error(mediaAltTextData.error || 'Failed to sync media alt text');
      }

      // Build detailed success message
      const stats = {
        products: productsData.stats,
        inventory: inventoryData.stats,
        media: mediaData.stats,
        copy: copyData.stats,
        product_translations: translationsData.stats,
        media_alt_text: mediaAltTextData.stats,
      };

      const totalCreated = (stats.products?.created || 0) + (stats.inventory?.created || 0) + (stats.media?.created || 0) + (stats.copy?.created || 0) + (stats.product_translations?.created || 0) + (stats.media_alt_text?.created || 0);
      const totalUpdated = (stats.products?.updated || 0) + (stats.inventory?.updated || 0) + (stats.media?.updated || 0) + (stats.copy?.updated || 0) + (stats.product_translations?.updated || 0) + (stats.media_alt_text?.updated || 0);
      const totalDeleted = (stats.products?.deleted || 0) + (stats.inventory?.deleted || 0) + (stats.media?.deleted || 0) + (stats.copy?.deleted || 0) + (stats.product_translations?.deleted || 0) + (stats.media_alt_text?.deleted || 0);

      setSyncStatus({
        type: 'success',
        message: `Sync complete! ${totalCreated} created, ${totalUpdated} updated${totalDeleted > 0 ? `, ${totalDeleted} deleted` : ''} across all tables.`,
      });

      console.log('[Sync] Detailed results:', stats);

      // Refresh comparison to show no more changes
      await fetchComparison();
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to sync to production',
      });
    } finally {
      setSyncing(false);
    }
  }

  const features = [
    {
      title: 'Products',
      description: 'Manage product catalog, descriptions, and pricing',
      icon: '📦',
      href: '/cms/products',
      active: true,
    },
    {
      title: 'Inventory',
      description: 'Update stock levels and availability',
      icon: '📊',
      href: '/cms/inventory',
      active: true,
    },
    {
      title: 'Media Library',
      description: 'Upload and manage product images, videos, tags, and alt text',
      icon: '🖼️',
      href: '/cms/media',
      active: true,
    },
    {
      title: 'Task Board',
      description: 'Plan sprints, track daily tasks, and manage your production schedule',
      icon: '📋',
      href: '/cms/tasks',
      active: true,
    },
    {
      title: 'AI Agents',
      description: 'Specialist agents for pricing, page copy, product SEO, and marketing campaigns',
      icon: '🤖',
      href: '/cms/ai-assistant',
      active: true,
    },
    {
      title: 'Campaigns',
      description: 'Manage Meta Ads and marketing campaigns',
      icon: '📢',
      href: '/cms/campaigns',
    },
    {
      title: 'Analytics',
      description: 'View sales, traffic, and customer insights',
      icon: '📈',
      href: '/cms/analytics',
    },
  ];

  return (
    <div className="min-h-screen bg-brand-sand dark:bg-brand-dark">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm dark:bg-brand-charcoal/80 dark:shadow-none dark:border-b dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="text-sm text-brand-gray hover:text-brand-blue dark:text-white/60 dark:hover:text-brand-blue flex items-center gap-2 transition-colors"
              >
                ← View Store
              </a>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-brand-dark dark:text-white">CMS Dashboard</h1>
            <p className="text-brand-gray dark:text-white/60 mt-1">Manage your store • {user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition"
          >
            Log Out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <button
              key={feature.title}
              onClick={() => navigate(feature.href)}
              className={`block p-6 bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm hover:shadow-md dark:shadow-none border transition text-left ${
                feature.active
                  ? 'border-brand-blue/40 hover:border-brand-blue dark:border-brand-blue/30 dark:hover:border-brand-blue'
                  : 'border-black/5 hover:border-brand-blue/30 dark:border-white/10 dark:hover:border-brand-blue/30'
              }`}
            >
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h2 className="text-xl font-bold text-brand-dark dark:text-white mb-2">{feature.title}</h2>
              <p className="text-brand-gray dark:text-white/70">{feature.description}</p>
              <div className={`mt-4 font-semibold ${
                feature.active
                  ? 'text-brand-blue flex items-center gap-2'
                  : 'text-brand-gray/50 dark:text-white/30'
              }`}>
                {feature.active ? (
                  <>
                    <span className="inline-block w-2 h-2 bg-brand-blue rounded-full animate-pulse"></span>
                    Open Dashboard
                  </>
                ) : (
                  '🔒 Coming Soon'
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Status Section */}
        <div className="mt-12 p-6 bg-brand-blue/10 dark:bg-brand-blue/5 rounded-xl border border-brand-blue/20 dark:border-brand-blue/15">
          <h3 className="text-lg font-bold text-brand-dark dark:text-white mb-2">✓ System Status</h3>
          <ul className="space-y-2 text-brand-dark/80 dark:text-white/80">
            <li>✓ Authentication system active (7-day sessions)</li>
            <li>✓ Rate limiting enabled on all endpoints</li>
            <li>✓ Input sanitization and XSS protection active</li>
            <li>✓ Neon Postgres (staging + production branches)</li>
            <li>✓ Cloudinary CDN ready for media uploads</li>
          </ul>
        </div>

        {/* Sync to Production Section */}
        <div className="mt-8 p-6 bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl border border-black/5 dark:border-white/10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-brand-dark dark:text-white mb-1">
                🚀 Push to Production
              </h3>
              <p className="text-sm text-brand-gray dark:text-white/60">
                Compare staging vs production and sync your changes to the live site.
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={fetchComparison}
                disabled={comparing}
                className="px-4 py-2 text-sm border border-black/15 dark:border-white/15 rounded-lg text-brand-dark dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                {comparing ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                onClick={handleSyncToProduction}
                disabled={syncing || !comparison || comparison.totalChanges === 0}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  syncing || !comparison || comparison.totalChanges === 0
                    ? 'bg-brand-gray/30 cursor-not-allowed text-brand-gray dark:bg-white/10 dark:text-white/30'
                    : 'bg-brand-blue hover:bg-brand-dark text-white'
                }`}
              >
                {syncing ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Syncing...
                  </span>
                ) : (
                  'Push to Production'
                )}
              </button>
            </div>
          </div>

          {/* Status Messages */}
          {syncStatus && (
            <div className={`mb-4 p-3 rounded-lg ${
              syncStatus.type === 'success'
                ? 'bg-green-100 dark:bg-green-950/50 border border-green-300 dark:border-green-900 text-green-800 dark:text-green-200'
                : 'bg-red-100 dark:bg-red-950/50 border border-red-300 dark:border-red-900 text-red-800 dark:text-red-200'
            }`}>
              {syncStatus.message}
            </div>
          )}

          {compareError && (
            <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-950/50 border border-red-300 dark:border-red-900 text-red-800 dark:text-red-200">
              {compareError}
            </div>
          )}

          {comparison?.warnings && comparison.warnings.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-amber-100 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-900 text-amber-800 dark:text-amber-200">
              <p className="font-semibold mb-1">Warnings (some tables could not be read):</p>
              {comparison.warnings.map((w, i) => (
                <p key={i} className="text-sm font-mono">{w}</p>
              ))}
            </div>
          )}

          {/* Comparison Results */}
          {comparing ? (
            <div className="flex items-center justify-center py-8 text-brand-gray dark:text-white/50">
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Comparing databases...
            </div>
          ) : comparison ? (
            <div className="space-y-4">
              {/* Summary */}
              {comparison.totalChanges === 0 ? (
                <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg text-center">
                  <span className="text-2xl">✓</span>
                  <p className="font-semibold text-green-800 dark:text-green-200 mt-1">
                    Staging and Production are in sync!
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    No changes to push.
                  </p>
                </div>
              ) : (
                <>
                  <div className="p-4 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 rounded-lg">
                    <p className="font-semibold text-orange-800 dark:text-orange-200">
                      {comparison.totalChanges} change{comparison.totalChanges !== 1 ? 's' : ''} pending
                    </p>
                    <p className="text-sm text-orange-700 dark:text-orange-300">
                      Review the changes below before pushing to production.
                    </p>
                  </div>

                  {/* Table Comparisons */}
                  {(['products', 'inventory', 'media', 'copy', 'product_translations', 'media_alt_text'] as const).map((table) => {
                    const data = comparison.comparison[table];
                    const hasChanges = data.added + data.modified + data.deleted > 0;
                    const isExpanded = expandedTable === table;

                    if (!hasChanges) return null;

                    const tableLabel = table === 'product_translations' ? 'Translations'
                      : table === 'media_alt_text' ? 'Media Alt Text'
                      : table.charAt(0).toUpperCase() + table.slice(1);

                    return (
                      <div key={table} className="border border-black/10 dark:border-white/10 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between p-4 bg-black/5 dark:bg-white/5">
                          <button
                            onClick={() => setExpandedTable(isExpanded ? null : table)}
                            className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                          >
                            <span className="text-lg font-medium text-brand-dark dark:text-white">
                              {tableLabel}
                            </span>
                            <div className="flex gap-2">
                              {data.added > 0 && (
                                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                                  +{data.added} new
                                </span>
                              )}
                              {data.modified > 0 && (
                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                                  {data.modified} modified
                                </span>
                              )}
                              {data.deleted > 0 && (
                                <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                                  {data.deleted} deleted
                                </span>
                              )}
                            </div>
                            <svg
                              className={`w-5 h-5 text-brand-gray dark:text-white/50 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSyncTable(table); }}
                            disabled={syncing || syncingTable !== null}
                            className="ml-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-blue hover:bg-brand-dark text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {syncingTable === table ? 'Syncing...' : `Sync ${tableLabel}`}
                          </button>
                        </div>

                        {isExpanded && (
                          <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                            {data.records.map((record) => (
                              <div
                                key={record.id}
                                className={`rounded-lg border overflow-hidden ${
                                  record.status === 'added'
                                    ? 'border-green-300 dark:border-green-700'
                                    : record.status === 'modified'
                                    ? 'border-blue-300 dark:border-blue-700'
                                    : 'border-red-300 dark:border-red-700'
                                }`}
                              >
                                {/* Record Header */}
                                <div className={`flex items-center gap-3 px-3 py-2 ${
                                  record.status === 'added'
                                    ? 'bg-green-100 dark:bg-green-900/50'
                                    : record.status === 'modified'
                                    ? 'bg-blue-100 dark:bg-blue-900/50'
                                    : 'bg-red-100 dark:bg-red-900/50'
                                }`}>
                                  {/* Media thumbnail */}
                                  {record.thumbnail && (
                                    <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-black/10">
                                      {record.mediaType === 'video' ? (
                                        <video src={record.thumbnail} className="w-full h-full object-cover" muted preload="metadata" />
                                      ) : (
                                        <img src={record.thumbnail} alt="" className="w-full h-full object-cover" />
                                      )}
                                    </div>
                                  )}
                                  <span className="font-medium text-brand-dark dark:text-white flex-1 truncate">
                                    {record.name || record.id}
                                  </span>
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded flex-shrink-0 ${
                                    record.status === 'added'
                                      ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                                      : record.status === 'modified'
                                      ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                                      : 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                                  }`}>
                                    {record.status}
                                  </span>
                                </div>

                                {/* GitHub-style Diff for Modified Records */}
                                {record.changes && record.changes.length > 0 && (() => {
                                  // Skip noisy/redundant fields when showing media diffs
                                  const MEDIA_SKIP_FIELDS = ['cloudinaryUrl', 'cloudinaryPublicId', 'fileHash', 'fileSize'];
                                  const visibleChanges = record.thumbnail
                                    ? record.changes.filter(c => !MEDIA_SKIP_FIELDS.includes(c.field))
                                    : record.changes;

                                  if (visibleChanges.length === 0) return null;

                                  const FIELD_LABELS: Record<string, string> = {
                                    mediaCount: 'Linked Media',
                                    linkedProducts: 'Linked Products',
                                    basePrice: 'Production Cost',
                                    'SEO-description': 'SEO Description',
                                    oneOfKind: 'One of a Kind',
                                    hasVariants: 'Has Variants',
                                    weightGrams: 'Weight (g)',
                                    materialCostPerGram: 'Material Cost/g',
                                  };

                                  return (
                                    <div className="bg-black/5 dark:bg-white/5 font-mono text-xs">
                                      {visibleChanges.map((change, idx) => {
                                        const formatValue = (val: any, field?: string) => {
                                          if (val === undefined || val === null) return '(empty)';
                                          if (field === 'mediaCount') return `${val} media item${val === 1 ? '' : 's'}`;
                                          if (Array.isArray(val)) return val.length === 0 ? '(none)' : val.join(', ');
                                          if (typeof val === 'object') return JSON.stringify(val).slice(0, 100);
                                          return String(val).slice(0, 100);
                                        };

                                        return (
                                          <div key={idx} className="border-t border-gray-200 dark:border-gray-700">
                                            <div className="px-3 py-1 text-brand-gray dark:text-white/60 bg-black/5 dark:bg-white/5 font-medium">
                                              {FIELD_LABELS[change.field] || change.field}
                                            </div>
                                            <div className="px-3 py-1.5 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 flex">
                                              <span className="text-red-500 mr-2 select-none">−</span>
                                              <span className="break-all">{formatValue(change.production, change.field)}</span>
                                            </div>
                                            <div className="px-3 py-1.5 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 flex">
                                              <span className="text-green-500 mr-2 select-none">+</span>
                                              <span className="break-all">{formatValue(change.staging, change.field)}</span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}

                                {/* New Record indicator */}
                                {record.status === 'added' && (
                                  <div className="px-3 py-2 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 text-xs">
                                    This record will be created in production
                                  </div>
                                )}

                                {/* Deleted Record indicator */}
                                {record.status === 'deleted' && (
                                  <div className="px-3 py-2 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 text-xs">
                                    This record exists in production but not in staging — will be removed on sync
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
